import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Settings,
  Crown,
  LogOut,
  ChevronDown,
  HelpCircle,
  UserCircle,
  BookOpen,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { ContactModal } from '../../components/ContactModal';

interface DashboardHeaderProps {
  userName: string | undefined;
  userEmail?: string;
  currentPlan: any;
  subscriptionStatus: any;
  isAdmin: boolean;
  onLogout: () => void;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  userName,
  userEmail,
  currentPlan,
  subscriptionStatus,
  isAdmin,
  onLogout,
}) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get user initials for avatar
  const getInitials = (name: string | undefined) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <>
      <header className="page-header sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 sm:py-6">
            <div className="animate-fade-in">
              <div className="flex items-center space-x-3">
                <img
                  src="/branding/competepages-logo.png"
                  alt="CompetePages"
                  className="h-10 w-auto object-contain"
                />
                <div>
                  <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                    {/* Hide welcome message on mobile */}
                    <p className="hidden sm:block text-gray-600">Welcome back, {userName}</p>
                    {/* Hide plan badge on mobile - visible in dropdown */}
                    {currentPlan && (
                      <span
                        className={cn(
                          'hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                          subscriptionStatus?.canceledAt
                            ? 'bg-yellow-100 text-yellow-800'
                            : currentPlan.name === 'FREE'
                              ? 'bg-gray-100 text-gray-800'
                              : currentPlan.name === 'PROFESSIONAL'
                                ? 'bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800'
                                : 'bg-blue-100 text-blue-800'
                        )}
                      >
                        {currentPlan.name === 'PROFESSIONAL' && !subscriptionStatus?.canceledAt && (
                          <Crown className="w-3 h-3 mr-1" />
                        )}
                        {currentPlan.displayName} Plan
                        {subscriptionStatus?.canceledAt && (
                          <span className="ml-1">
                            (Ends{' '}
                            {new Date(subscriptionStatus.currentPeriodEnd).toLocaleDateString()})
                          </span>
                        )}
                      </span>
                    )}
                    {/* Hide admin badge on mobile - visible in dropdown */}
                    {isAdmin && (
                      <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                        Admin
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* User Menu Dropdown */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center space-x-2 p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-expanded={isUserMenuOpen}
                aria-haspopup="true"
              >
                {/* User Avatar */}
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 flex items-center justify-center text-white text-sm font-medium">
                  {getInitials(userName)}
                </div>
                {/* Show name and chevron on larger screens */}
                <span className="hidden md:block text-sm font-medium text-gray-700 max-w-[120px] truncate">
                  {userName}
                </span>
                <ChevronDown
                  size={16}
                  className={cn(
                    'hidden sm:block text-gray-500 transition-transform',
                    isUserMenuOpen && 'rotate-180'
                  )}
                />
              </button>

              {/* Dropdown Menu */}
              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {/* User Info Header */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900 truncate">{userName}</p>
                    {currentPlan && (
                      <p className="text-xs text-gray-500 mt-0.5">{currentPlan.displayName} Plan</p>
                    )}
                  </div>

                  {/* Menu Items */}
                  <div className="py-1">
                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Settings size={16} className="mr-3 text-gray-400" />
                        Admin Dashboard
                      </Link>
                    )}
                    <Link
                      to="/help"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <BookOpen size={16} className="mr-3 text-gray-400" />
                      Help Guides
                    </Link>
                    <Link
                      to="/account"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <UserCircle size={16} className="mr-3 text-gray-400" />
                      Account settings
                    </Link>
                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        setIsContactOpen(true);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <HelpCircle size={16} className="mr-3 text-gray-400" />
                      Support
                    </button>
                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        onLogout();
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <LogOut size={16} className="mr-3 text-gray-400" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      <ContactModal
        isOpen={isContactOpen}
        onClose={() => setIsContactOpen(false)}
        initialName={userName}
        initialEmail={userEmail}
      />
    </>
  );
};
